import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail, Lock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword, updatePasswordWithToken } = useAuth();
  const { toast } = useToast();
  
  const [step, setStep] = useState<'email' | 'reset' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  // Check if we have a password reset token in the URL
  useEffect(() => {
    // Check URL hash for password reset token
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    
    if (accessToken && type === 'recovery') {
      console.log('🔐 Password reset token found in URL');
      // User clicked the password reset link
      // Supabase automatically processes the token from the URL hash
      // We need to wait for the session to be established
      
      let subscription: { unsubscribe: () => void } | null = null;
      let timeoutId: NodeJS.Timeout | null = null;
      let retryTimeoutId: NodeJS.Timeout | null = null;
      
      // Listen for auth state changes to detect when session is established
      const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state changed during password reset:', event);
          
          if (event === 'PASSWORD_RECOVERY') {
            console.log('✅ PASSWORD_RECOVERY event detected');
            setStep('reset');
            if (subscription) {
              subscription.unsubscribe();
              subscription = null;
            }
          } else if (session) {
            console.log('✅ Password reset session established');
            setStep('reset');
            if (subscription) {
              subscription.unsubscribe();
              subscription = null;
            }
          }
        }
      );
      
      subscription = authSubscription;
      
      // Also check for existing session after a short delay
      // Supabase processes the token automatically from the URL hash
      timeoutId = setTimeout(async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            console.log('✅ Password reset session found');
            setStep('reset');
            if (subscription) {
              subscription.unsubscribe();
              subscription = null;
            }
          } else {
            console.warn('⚠️ No session found after password reset token, waiting...');
            // Try again after a longer delay
            retryTimeoutId = setTimeout(async () => {
              const { data: { session: retrySession } } = await supabase.auth.getSession();
              if (retrySession) {
                console.log('✅ Password reset session found on retry');
                setStep('reset');
              } else {
                console.error('❌ No session found after password reset token');
                toast({
                  title: "Invalid token",
                  description: "The password reset link is invalid or has expired. Please request a new one.",
                  variant: "destructive",
                });
              }
              if (subscription) {
                subscription.unsubscribe();
                subscription = null;
              }
            }, 2000);
          }
        } catch (error) {
          console.error('Error checking password reset token:', error);
        }
      }, 500);
      
      // Cleanup on unmount
      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
        }
      };
    }
  }, [toast]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): boolean => {
    // At least 8 characters, one uppercase, one lowercase, one number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    if (!email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      console.log('📧 Sending password reset email to:', email);
      const result = await resetPassword(email);

      if (!result.success) {
        toast({
          title: "Error",
          description: result.error || "Failed to send password reset email. Please try again.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Success - show confirmation message
      toast({
        title: "Email sent!",
        description: "Please check your email for password reset instructions.",
      });
      setStep('success');
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send password reset email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    const newErrors: typeof errors = {};
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(password)) {
      newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      console.log('🔐 Resetting password');
      const result = await updatePasswordWithToken(password);

      if (!result.success) {
        toast({
          title: "Error",
          description: result.error || "Failed to reset password. Please try again.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Success - show confirmation and redirect to login
      toast({
        title: "Password reset!",
        description: "Your password has been reset successfully. Please sign in with your new password.",
      });
      
      // Redirect to login after a short delay
      // Set sessionStorage flag BEFORE navigation to ensure it's available
      console.log('🔙 Setting fromForgotPassword flag for redirect to login');
      sessionStorage.setItem('fromForgotPassword', 'true');
      setTimeout(() => {
        console.log('🔙 Navigating to login from password reset success');
        navigate('/login', { replace: true });
      }, 2000);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Card className="p-8 shadow-lg">
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center space-y-2">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  {step === 'success' ? (
                    <CheckCircle className="h-8 w-8 text-primary" />
                  ) : step === 'reset' ? (
                    <Lock className="h-8 w-8 text-primary" />
                  ) : (
                    <Mail className="h-8 w-8 text-primary" />
                  )}
                </div>
              </div>
              <h1 className="text-2xl font-bold">
                {step === 'success' 
                  ? 'Check your email' 
                  : step === 'reset'
                  ? 'Reset your password'
                  : 'Forgot password?'}
              </h1>
              <p className="text-muted-foreground">
                {step === 'success'
                  ? 'We sent password reset instructions to your email address.'
                  : step === 'reset'
                  ? 'Enter your new password below.'
                  : "Enter your email address and we'll send you instructions to reset your password."}
              </p>
            </div>

            {/* Email Step */}
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors({ ...errors, email: undefined });
                    }}
                    className="h-14 text-lg"
                    autoFocus
                    required
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">{errors.email}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 text-base font-semibold rounded-xl"
                  disabled={loading || !email}
                >
                  {loading ? "Sending..." : "Send reset instructions"}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      // Set sessionStorage flag BEFORE navigation to ensure it's available
                      console.log('🔙 Setting fromForgotPassword flag and navigating to login');
                      sessionStorage.setItem('fromForgotPassword', 'true');
                      // Small delay to ensure sessionStorage is set
                      setTimeout(() => {
                        navigate('/login', { replace: true });
                      }, 0);
                    }}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Back to login
                  </button>
                </div>
              </form>
            )}

            {/* Reset Password Step */}
            {step === 'reset' && (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your new password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors({ ...errors, password: undefined });
                    }}
                    className="h-14 text-lg"
                    autoFocus
                    required
                  />
                  {errors.password && (
                    <p className="text-sm text-destructive mt-1">{errors.password}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrors({ ...errors, confirmPassword: undefined });
                    }}
                    className="h-14 text-lg"
                    required
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-destructive mt-1">{errors.confirmPassword}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 text-base font-semibold rounded-xl"
                  disabled={loading || !password || !confirmPassword}
                >
                  {loading ? "Resetting..." : "Reset password"}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      // Set sessionStorage flag BEFORE navigation to ensure it's available
                      console.log('🔙 Setting fromForgotPassword flag and navigating to login');
                      sessionStorage.setItem('fromForgotPassword', 'true');
                      // Small delay to ensure sessionStorage is set
                      setTimeout(() => {
                        navigate('/login', { replace: true });
                      }, 0);
                    }}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Back to login
                  </button>
                </div>
              </form>
            )}

            {/* Success Step */}
            {step === 'success' && (
              <div className="space-y-4">
                <div className="text-center space-y-4">
                  <div className="p-4 bg-primary/10 rounded-lg space-y-3">
                    <p className="text-sm text-muted-foreground">
                      If an account exists with this email, you will receive password reset instructions shortly.
                    </p>
                    <div className="pt-2 border-t border-primary/20">
                      <p className="text-sm text-foreground font-medium">
                        Don't see the email? Please check your <strong className="text-primary">spam</strong> or <strong className="text-primary">junk</strong> folder.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    // Set sessionStorage flag BEFORE navigation to ensure it's available
                    console.log('🔙 Setting fromForgotPassword flag and navigating to login');
                    sessionStorage.setItem('fromForgotPassword', 'true');
                    // Small delay to ensure sessionStorage is set
                    setTimeout(() => {
                      navigate('/login', { replace: true });
                    }, 0);
                  }}
                  className="w-full h-14 text-base font-semibold rounded-xl"
                >
                  Back to login
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setEmail('');
                    }}
                    className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    Didn't receive the email? Try again
                  </button>
                </div>
              </div>
            )}

          </div>
        </Card>
      </div>
    </div>
  );
}

